import { ChangeEvent, KeyboardEvent, MouseEvent, ReactNode } from 'react'
import classNames from 'classnames'
import { FORM_CONTROL_FOCUS_RING } from '../../../lib/a11yFocus'
import Label from '../Label'
import SearchInput from '../Forms/SearchInput'
import Button from '../Button/Button'

export type PageControlBarRadioOption = {
	id: string
	label: string
	checked: boolean
	onChange: () => void
}

type PageControlBarModuleProps = {
	children: ReactNode
	additionalClass?: string
}

const PageControlBarModule = ({ children, additionalClass }: PageControlBarModuleProps) => {
	return (
		<div
			data-testid='page-control-bar'
			className={classNames('mt-5', additionalClass)}
		>
			{children}
		</div>
	)
}

type PrimaryProps = {
	children: ReactNode
}

const Primary = ({ children }: PrimaryProps) => {
	return (
		<div className='flex items-end gap-x-5 flex-wrap' data-testid='page-control-bar-primary'>
			{children}
		</div>
	)
}

type SelectorProps = {
	label: string
	children: ReactNode
}

const Selector = ({ label, children }: SelectorProps) => {
	return (
		<div className='flex flex-col justify-end' data-testid='page-control-bar-selector'>
			<Label className='block'>{label}</Label>
			{children}
		</div>
	)
}

type SearchProps = {
	name: string
	label: string
	placeholder?: string
	value: string
	onChange: (event: ChangeEvent<HTMLInputElement>) => void
	onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void
	onClear?: () => void
	onSearch?: () => void
	isLoading?: boolean
	className?: string
}

const Search = ({
	name,
	label,
	placeholder,
	value,
	onChange,
	onKeyDown,
	onClear,
	onSearch,
	isLoading = false,
	className = 'w-[345px]',
}: SearchProps) => {
	return (
		<div className={classNames('self-end min-w-[175px]', className)} data-testid='page-control-bar-search'>
			<div className='flex flex-col justify-end w-full'>
			<Label className='sr-only'>{label}</Label>
				<SearchInput
					name={name}
					placeholder={placeholder}
					value={value}
					onChange={onChange}
					onKeyDown={onKeyDown}
					onClear={onClear}
					onSearch={onSearch}
					isLoading={isLoading}
					className='w-full'
				/>
			</div>
		</div>
	)
}

type RadioGroupProps = {
	label: string
	name: string
	options: PageControlBarRadioOption[]
}

const RadioGroup = ({ label, name, options }: RadioGroupProps) => {
	return (
		<div className='flex flex-col justify-end' data-testid='page-control-bar-radio-group'>
			{/* Flex/height live on an inner div, not the fieldset: Chrome sizes a fieldset's anonymous content box to its content, so items-center on the fieldset itself leaves the radios top-aligned. h-[42px] matches the input/button height. */}
			<fieldset className='text-white'>
				<legend className='sr-only'>{label}</legend>
				<div className='flex items-center h-[42px] space-x-4'>
					{options.map((option) => (
						<label
							key={option.id}
							className='flex items-center cursor-pointer'
						>
							<input
								name={name}
								type='radio'
								checked={option.checked}
								onChange={option.onChange}
								className={`h-4 w-4 border-white text-algae-500 cursor-pointer active:ring-2 active:ring-transparent ${FORM_CONTROL_FOCUS_RING}`}
							/>
							<span className='ml-3 block text-sm text-white'>{option.label}</span>
						</label>
					))}
				</div>
			</fieldset>
		</div>
	)
}

type ActionsProps = {
	children: ReactNode
}

const Actions = ({ children }: ActionsProps) => {
	return (
		<div
			className='grow flex flex-col justify-end items-end'
			data-testid='page-control-bar-actions'
		>
			<div className='flex items-center'>{children}</div>
		</div>
	)
}

type FiltersProps = {
	children: ReactNode
}

const Filters = ({ children }: FiltersProps) => {
	return (
		<div
			className='flex items-end gap-5 flex-wrap mt-4'
			data-testid='page-control-bar-filters'
		>
			{children}
		</div>
	)
}

type ControlButtonProps = {
	type: 'Primary' | 'Secondary' | 'White' | 'on dark' | 'Green-Secondary' | 'Outlined' | 'Link' | 'Pagination'
	text: string
	onClick?: (event: MouseEvent) => void
	disabled?: boolean
	icon?: string
	hasLeadingIcon?: boolean
	hasTrailingIcon?: boolean
	additionalClass?: string
	ariaControls?: string
	ariaExpanded?: boolean
	buttonType?: 'button' | 'submit'
	accessibilityLabel?: string
	id?: string
	requiresWrite?: boolean
}

const ControlButton = ({
	type,
	text,
	onClick,
	disabled = false,
	icon,
	hasLeadingIcon = false,
	hasTrailingIcon = false,
	additionalClass,
	ariaControls,
	ariaExpanded,
	buttonType = 'button',
	accessibilityLabel,
	id,
	requiresWrite,
}: ControlButtonProps) => {
	// Apply canonical control height (42px) directly to button, matching SearchInput pattern.
	// flex is required to make the button's internal flex layout stretch content vertically.
	const buttonClasses = additionalClass 
		? `h-[42px] flex ${additionalClass}`
		: 'h-[42px] flex'

	return (
		<Button
			type={type}
			text={text}
			size='sm'
			onClick={onClick}
			disabled={disabled}
			icon={icon}
			hasLeadingIcon={hasLeadingIcon}
			hasTrailingIcon={hasTrailingIcon}
			additionalClass={buttonClasses}
			ariaControls={ariaControls}
			ariaExpanded={ariaExpanded}
			buttonType={buttonType}
			accessibilityLabel={accessibilityLabel}
			id={id}
			requiresWrite={requiresWrite}
		/>
	)
}

PageControlBarModule.Primary = Primary
PageControlBarModule.Selector = Selector
PageControlBarModule.Search = Search
PageControlBarModule.RadioGroup = RadioGroup
PageControlBarModule.Actions = Actions
PageControlBarModule.Filters = Filters
PageControlBarModule.Button = ControlButton

export default PageControlBarModule
